import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useRef, useState } from 'react';
import { debounce } from 'lodash';
import { useNotificationsStore, HelpIcon } from '@thermon-platforms/thermon-ui-core';
import api, { parseErrors } from 'util/api';


function ResetPassword({user}) {
  const form = useRef();
  const [, notificationsStoreActions] = useNotificationsStore();
  const [newPassword, setNewPassword] = useState({});
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState();

  const handlePasswordChange = debounce((value, key) => {
    // When the input changes, clear errors
    // NOTE - we may want to make this more targeted
    setErrors(undefined);
    // Create a new object so setting state works
    const passwordObj = Object.assign({}, newPassword);
    passwordObj[key] = value;
    // Set the new state
    setNewPassword(passwordObj);
  }, 200);

  async function updatePassword(event) {
    event.preventDefault();

    setLoading(true);
    // Handle any errors
    if (newPassword.newPassword != newPassword.confirmPassword) {
      setLoading(false);
      setErrors({
        confirmPassword: t`Passwords do not match`
      });
      return;
    }
    // Fake request delay
    // await new Promise(resolve => setTimeout(resolve, 500));
    api(`/users/${user.id}/password`, {
      method: 'PUT',
      body: JSON.stringify(newPassword)
    })
      .then(() => {
        // Notify the user we succeeded
        notificationsStoreActions.notify(t`Password updated`, 'ok');
        // Clear the state
        setNewPassword({});
        // Clear the UI
        form.current.reset();
      })
      .catch(async res => {
        const newErrors = parseErrors(await res.json());
        if (newErrors.PasswordRequiresNonAlphanumeric) {
          setErrors({
            newPassword: newErrors.PasswordRequiresNonAlphanumeric
          });
          return;
        }
        if (newErrors.PasswordRequiresUpper) {
          setErrors({
            newPassword: newErrors.PasswordRequiresUpper
          });
          return;
        }
        if (newErrors.PasswordMismatch) {
          setErrors({
            currentPassword: newErrors.PasswordMismatch
          });
          return;
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <form name="change-password" key="change-password" ref={form} onSubmit={updatePassword}>
      <ul>
        <li>
          <label htmlFor="currentPassword"><Trans>Current Password</Trans></label>
          <input
            type="password"
            id="currentPassword"
            name="currentPassword"
            autoComplete="off"
            defaultValue={newPassword.currentPassword}
            className={errors?.currentPassword ? 'error' : ''}
            onChange={event => handlePasswordChange(event.target.value, 'currentPassword')}
          />
          {errors?.currentPassword ?
            <p className="error-text">{errors?.currentPassword}</p>
          : <></>}
        </li>
        <li>
          <label htmlFor="newPassword">
            <Trans>New Password</Trans>
            <HelpIcon
              text={t`Password must be at least 6 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.`}
            />
          </label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            autoComplete="new-password"
            defaultValue={newPassword.newPassword}
            className={errors?.newPassword ? 'error' : ''}
            onChange={event => handlePasswordChange(event.target.value, 'newPassword')}
          />
          {errors?.newPassword ?
            <p className="error-text">{errors?.newPassword}</p>
          : <></>}
        </li>
        <li>
          <label htmlFor="confirmPassword"><Trans>Confirm New Password</Trans></label>
          <input
            type="password"
            id="confirmPassword"
            name="confirmPassword"
            className={errors?.confirmPassword ? 'error' : ''}
            autoComplete="new-password"
            defaultValue={newPassword.confirmPassword}
            onChange={event => handlePasswordChange(event.target.value, 'confirmPassword')}
          />
          {errors?.confirmPassword ?
            <p className="error-text">{errors?.confirmPassword}</p>
          : <></>}
        </li>
        <li>
          <button
            className={loading ? 'processing' : ''}
            disabled={loading || (!newPassword.currentPassword || !newPassword.newPassword || !newPassword.confirmPassword)}
          >
            <Trans>Update</Trans>
          </button>
        </li>
      </ul>
    </form>
  );
}

export default ResetPassword;
